/* eslint-disable camelcase */
import { WithApolloClient } from '@apollo/client/react/hoc';

import type { Me } from '@crehana/web/dist/ReactContexts/AuthContext';
import { CountryPrefix } from '@crehana/ts-types';

import { UserType } from 'Jsx/Types/UserType';
import { DocumentTypeEnum } from 'Jsx/Types/Graphql/globals.payments';
import {
  PaymentMutationVariables,
  PaymentMutation_payment as PaymentResponse,
} from 'Jsx/CheckoutV4/Graphql/Mutation/types/PaymentMutation';

import { PaymentSourceEnum } from 'Jsx/Types/Graphql/globals.payments';

import {
  GetProductItemQuery_item as QueryItem,
  GetProductItemQuery_item_degreeQuotas,
} from '../Graphql/Query/types/GetProductItemQuery';
import { GetSavedCustomerCardsQuery_customerCards as SavedCards } from '../Graphql/Query/types/GetSavedCustomerCardsQuery';

import { Stripe } from './StripeV2';
import { Ebanx } from './Ebanx';

declare global {
  interface Window {
    RECAPTCHA_PUBLIC_KEY: string;
    RAPPI_DEBUG_MODE: string;
    DLOCAL_DEBUG: string;
    payU: any;
    PAYU_PUBLIC_KEY: string;
    PAYU_SET_URL_API: string;
    PAYU_ACCOUNT_PE: string;
    paypal: any;
    PAYPAL_MODE: string;
    Stripe: Stripe;
    PINAX_STRIPE_PUBLIC_KEY: string;
    STRIPE_MX_SECRET_KEY: string;
    STRIPE_SECRET_KEY: string;
    CONEKTA_PUBLIC_KEY: string;
    EBANX_PUBLIC_INTEGRATION_KEY: string;
    PINAX_STRIPE_MEXICAN_PUBLIC_KEY: string;
    MERCADOPAGO_API_MX_PUBLIC_KEY: string;
    EBANX: Ebanx;
    EBANX_DEBUG: string;
    dlocal: any;
    PagaRappi: any;
    Conekta: any;
    DLOCAL_API_KEY: string;
    RAPPI_CO_CLIENT_ID: string;
    RAPPI_MX_CLIENT_ID: string;
    DEVICE_SESSION_ID: string;
  }
}

export type CreditCardIssuerType =
  | 'American Express'
  | 'Visa'
  | 'MasterCard'
  | 'Diners Club'
  | 'Discover'
  | 'JCB'
  | '';

export type SelectedCardType = {
  value: string;
  label: string;
};

export type ProductListItem = {
  originalId: number;
  title: string;
  sku: number;
  type: string;
  description: string;
  image: string;
  professorFullname: string;
  prices: Prices;
  professor: {
    fullName: string;
  };
};

export type StepsToBuy = {
  order?: number;
  label: string;
  status: string;
};

export type CheckoutContextType = {
  loading?: boolean;
  guestEmail: GuestEmailType;
  isGuestCheckout: boolean;
  onEmailChange: (email: CustomEmail) => void;
  handleOnblurEmail: () => void;
  paymentSource?: PaymentSourceEnum;
  preloadedCourseData: string;
  recipientName?: string;
  checkoutProductDetail: ProductItem | null;
  isBootcamp: boolean;
  handleApplyCoupon(coupon: { couponCode: string }): Promise<{
    success: boolean;
    errors: any;
  }>;
  handleRemoveCoupon: () => void;
  isFetchingPaymentMethods: boolean;
  setSelectedPaymentMethod: (value?: PaymentMethodType) => void;
  selectedPaymentMethod?: PaymentMethodType;
  setSelectedOfflinePaymentMethod: (value: PaymentMethodType) => void;
  selectedOfflinePaymentMethod?: PaymentMethodType;
  setStepsToBuy: (value: StepsToBuy[]) => void;
  offlinePaymentMethods?: PaymentMethods;
  buyNowMutation: (value: {
    variables: PaymentMutationVariables;
  }) => Promise<PaymentResponse | void | null>;
  countryPrefix: CountryPrefix;
  paymentMethods: PaymentMethods;
  recaptchaToken: string;
  isQuickEnrollEnabled: boolean;
  checkoutProductsList?: ShoppingCartItem[];
  productData: ProductData;
  stepsToBuy?: StepsToBuy[];
  onlinePaymentMethods?: PaymentMethodType[];
  updatePaymentMethods: (paymentMethods: PaymentMethods) => void;
  paymentFormValues?: PaymentFormValues;
  setPaymentFormValues: (values: PaymentFormValues) => void;
  user: Me | UserType | null;
  showPaymenButtonRight: boolean;
  flags: {
    useNewCreditCardFeature: boolean;
  };
  fallbackToCreditCardPaymentMethod: (value?: PaymentMethodType) => void;
};

export type SelectedMethodType = {
  selectedMethod: {
    title: string;
  };
};
export type GuestEmailType = {
  email: string | any;
  errorTextEmail: any;
  isEmailValid: boolean;
};

export type CheckoutSectionProps = WithApolloClient<{
  itemData: {
    itemId: number;
    itemType: string;
  };
  paymentSource?: PaymentSources;
}>;

export type PaymentMethods = { [key: string]: PaymentMethodType | undefined };
export interface AppState {
  isFetchingCheckoutItemDetail: boolean;
  isFetchingPaymentMethods: boolean;
  paymentMethods: PaymentMethods;
  guestEmail: {
    email: string;
    errorTextEmail: any;
    isEmailValid: boolean;
  };
  checkoutProductDetail: ProductItem | null;
  checkoutProductsList?: ShoppingCartItem[];
  selectedPaymentMethod?: PaymentMethodType;
  selectedOfflinePaymentMethod?: PaymentMethodType;
  itemBeforeCouponApplication: ProductItem | null;
  stepsToBuy?: StepsToBuy[];
  offlinePaymentMethods?: PaymentMethods;
  onlinePaymentMethods?: PaymentMethodType[];
  paymentFormValues?: PaymentFormValues;
}

// enums
export enum StoreAvailable {
  CONEKTA_OXXO = 'oxxo_cash',
  OXXO = 'OXXO',
}

export enum CountryPrefixEnum {
  BRASIL = 'br',
  ARGENTINA = 'ar',
  PERU = 'pe',
  COLOMBIA = 'co',
  MEXICO = 'mx',
  CHILE = 'cl',
}

export enum PaymentSources {
  CHECKOUT_VIEW = 'CHECKOUT_VIEW',
  SHOPPING_CART_VIEW = 'SHOPPING_CART_VIEW',
  MEMBERSHIP_VIEW = 'MEMBERSHIP_VIEW',
  COURSE_DETAIL_VIEW = 'COURSE_DETAIL_VIEW',
  BUNDLE_DETAIL_VIEW = 'BUNDLE_DETAIL_VIEW',
  COURSE_DETAIL_POP_UP_VIEW = 'COURSE_DETAIL_POP_UP_VIEW',
  CUSTOM_BUNDLE_POP_UP_VIEW = 'CUSTOM_BUNDLE_POP_UP_VIEW',
  MEMBERSHIP_LANDING_POP_UP_VIEW = 'MEMBERSHIP_LANDING_POP_UP_VIEW',
}

export enum CreditCardIssuer {
  AMEX = 'American Express',
  DINERS = 'Diners Club',
}

export enum MembershipTagEnum {
  PREMIUM = 'PREMIUM',
  PREMIUM_DUO = 'PREMIUM_DUO',
  FAMILIAR = 'FAMILIAR',
}

export type PaymentSourcesType =
  | 'CHECKOUT_VIEW'
  | 'SHOPPING_CART_VIEW'
  | 'MEMBERSHIP_VIEW'
  | 'COURSE_DETAIL_VIEW'
  | 'BUNDLE_DETAIL_VIEW'
  | 'COURSE_DETAIL_POP_UP_VIEW'
  | 'CUSTOM_BUNDLE_POP_UP_VIEW'
  | 'MEMBERSHIP_LANDING_POP_UP_VIEW';

export type CustomEmailError = 'required' | 'invalid' | null;
export interface CustomEmail {
  value: string;
  username?: string;
  domain?: string;
  error: {
    code: CustomEmailError;
    message: string;
  };
  complete: boolean;
}

export type CustomerCard = Omit<SavedCards, '__typename'> | null;

export type UserDiscount = {
  typeDiscount: string;
  textLabel: string;
  textColor: string;
  backgroundColor: string;
  total: string;
  __typename?: string;
};

export type DegreeQuota = Omit<
  GetProductItemQuery_item_degreeQuotas,
  '__typename'
>;

export interface ProductItem extends QueryItem {
  originalId: number | null;
  subtotal: number | null;
  price: number | null;
  discountRate: number | null;
  terms: number | null;
  isQuickEnroll: boolean;
  couponCode: string;
  membershipStartedDate: string;
  giftcard: string;
  promotionToken: string;
}

export type ProductData = {
  giftCard?: { recipientName: string };
  isCouponEnabled?: boolean;
  itemId: number;
  itemType: ItemType;
  showContactNumber?: boolean;
  timer?: string;
  callCenter?: CallCenter;
  membershipTag?: MembershipTagEnum;
  degreeSyllabusData?: DegreeSyllabusData;
  exitPopupCoupon?: CouponData;
  incentiveData?: CouponData;
  licenseNumber?: number;
  popupPaymentFailedData?: CouponData;
  quantity?: number;
  pageCoupon?: {
    couponCode: string;
    isValid: boolean;
  };
  isValidPartnershipItem: boolean;
};

export type DegreeSyllabusData = {
  longFinishDate: string;
  longStartDate: string;
  shortFinishDate: string;
  shortStartDate: string;
};
export type CouponData = {
  couponCode: string;
  isValid: boolean;
  value: number;
};

export type Prices = {
  price: number;
  currencySymbol: string;
  realPrice: number;
};

export interface PaymentMethodType {
  paymentGatewayId?: number;
  title?: string;
  extraCommission?: number;
  logoMiniatureUrl?: string;
  logoUrl?: string;
  maxAmount?: number;
  offlineLabel?: string;
  slug?: string;
  text?: string;
  isSelected: boolean;
  isMethodWithText?: boolean;
}

export type CallCenter = {
  adviserName: string;
  adviserPhoto: string;
  customerName: string;
  expirationDate: string;
  token: string;
  hasExpired: boolean;
  additionalTimeText?: string;
  isValidDuringCall: boolean;
  additionalMonths?: number;
};

export type PopupOpening =
  | 'EXIT_CHECKOUT'
  | 'FAILED_PAYMENT'
  | 'COURSE'
  | 'CUSTOM_BUNDLE'
  | 'SHOPPING_CART'
  | 'MEMBERSHIPv';

export type PaymentMutationStatus =
  | 'success'
  | 'error'
  | 'pending'
  | 'card_declined'
  | 'processing_error'
  | null;

export type OfflinePurchaseDetail = {
  storeName: string;
  date: string;
  paymentCode: string;
  infoLink?: string;
  referenceCode?: string;
  localPrice?: string;
};
export interface DropdownOption {
  value: string;
  label: string;
}

export type AnotherPaymentMethod = {
  isCard: boolean;
  logoUrl: string;
  paymentGatewayId: number;
  text: string;
  title: string;
};

export interface NotificationUser {
  timeLabel?: string;
  image: string;
  name?: string;
  city: string;
}

export type ShoppingCartItem = {
  id: number;
  image: string;
  title: string;
};

export type ItemType =
  | 'course'
  | 'degree'
  | 'membership'
  | 'custom_bundle'
  | 'bundle'
  | 'shopping_cart';

export type PaymentFormValues = {
  creditCardNumber: {
    isComplete: boolean;
    value: string;
    brand?: CreditCardIssuerType;
  };
  creditCardExpiry: {
    isComplete: boolean;
    value: string;
  };
  creditCardCVV: {
    isComplete: boolean;
    value: string;
  };
  identityDocument?: {
    isComplete: boolean;
    type: DocumentTypeEnum;
    value: string;
  };
  termsAndConditions: {
    isComplete: boolean;
  };
  checkedNewsLetterTerms: {
    value: boolean;
  };
};
